import {
  Button,
  ButtonVariant,
  Option,
  SelectOption
} from '@focaldata/cin-ui-components'
import { useState } from 'react'
import { getDefaultCompletes } from '../../../controls/ProjectTypesPanel/ProjectTypesPanel.utils'
import { ProjectType } from '../../../data/gql-gen/dashboard/graphql'
import { useAudienceCountries } from '../../../hooks/audience/useAudienceCountries'
import useCreateProject from '../../../hooks/project/useCreateProject'
import {
  reorderCountriesDropDown,
  suggestedCountryOption
} from '../../../utils/HelperFunctions'
import { PanelSupplier } from '../../../utils/audienceHelper'
import CreateProjectDialog from './CreateProjectDialog'

const DEFAULT_OPTION: Option = {
  id: 'GB-132',
  countryCode: 'GB',
  name: 'United Kingdom',
  value: 'GB-en'
}

const getNewProjectButtonVariant = (projectType?: ProjectType) => {
  switch (projectType) {
    case ProjectType.AdHoc:
      return ButtonVariant.PrimaryFilled
    case ProjectType.MultiMarketV2:
      return ButtonVariant.SecondaryOutlined
    case ProjectType.FdChat:
      return ButtonVariant.SecondaryFilled
    default:
      return ButtonVariant.PrimaryOutlined
  }
}

const CreateProject = ({
  dialogTitle,
  type,
  hideCountry = false,
  hideCompletes = false,
  defaultCompletes,
  buttonText = 'Create new project'
}: {
  dialogTitle?: string
  type?: ProjectType
  hideCountry?: boolean
  hideCompletes?: boolean
  defaultCompletes?: number
  buttonText?: string
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [projectType, setProjectType] = useState(type ?? ProjectType.AdHoc)
  const [country, setCountry] = useState<SelectOption>(DEFAULT_OPTION)
  const [completes, setCompletes] = useState<number>(
    defaultCompletes ?? getDefaultCompletes(projectType)
  )
  const panelSupplierCode =
    projectType === ProjectType.FdChat
      ? PanelSupplier.PROLIFIC
      : PanelSupplier.PURESPECTRUM
  const { data: dataCountries } = useAudienceCountries(panelSupplierCode)

  const isMultiMarketProjectSelected = projectType === ProjectType.MultiMarketV2
  const shouldHideCountry = hideCountry || isMultiMarketProjectSelected
  const shouldHideCompletes = hideCompletes || isMultiMarketProjectSelected

  const {
    loadingGetUserAndAccount,
    loadingCreateProject,
    handleCreateProject
  } = useCreateProject()

  const countriesOptions = reorderCountriesDropDown(dataCountries ?? [])

  // TODO: Set it to "suggestedCountries(options)" when we have all countries
  const suggestedCountries = suggestedCountryOption(undefined)

  const getLanguageByValue = (value: string) => {
    const language = value.split('-')[1]
    return language
  }

  const handleButtonClick = () => {
    setIsDialogOpen(!isDialogOpen)
  }

  const variant = getNewProjectButtonVariant(type)

  return (
    <>
      <Button
        onClick={handleButtonClick}
        disabled={loadingGetUserAndAccount}
        dataCy="dialog-button-opener"
        style={{ width: 'max-content' }}
        variant={variant}
      >
        {buttonText}
      </Button>
      <CreateProjectDialog
        completes={completes}
        country={country}
        defaultOption={DEFAULT_OPTION}
        open={isDialogOpen}
        options={countriesOptions}
        setCompletes={shouldHideCompletes ? undefined : setCompletes}
        setProjectType={!type ? setProjectType : undefined}
        setCountry={shouldHideCountry ? undefined : setCountry}
        setOpen={setIsDialogOpen}
        suggestedOptions={suggestedCountries}
        primaryButtonClick={(title) =>
          handleCreateProject({
            title,
            projectType: type ?? projectType,
            countryCode: country.countryCode,
            countryLanguage: getLanguageByValue(country.value as string),
            samplesNeeded: completes
          })
        }
        loadingCreateProject={loadingCreateProject}
        title={dialogTitle}
        dataDdActionNamePrimaryButton={`Create new project (Dialog - ${
          type ?? projectType
        })`}
      />
    </>
  )
}

export default CreateProject
